import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Users, Building, Shield, Star, Receipt, Mail, Send, MapPin,
  CreditCard, Download, ArrowUpCircle, Settings, Tag, Layers,
  CheckCircle2, XCircle, Clock, AlertTriangle, Hash, Search,
  Factory, FileText, UserCircle, Power, Trash2, UserX, UserCheck,
  ChevronDown, ChevronRight, ShieldCheck, BookOpen
} from 'lucide-react';
import axios from 'axios';
import ApiDocsSection from '@/components/portal/ApiDocsSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stateConfig = {
  STANDARD_FREE: { label: 'Standard', color: 'bg-slate-100 text-slate-700', icon: Shield },
  TRIAL: { label: 'Testphase', color: 'bg-blue-100 text-blue-700', icon: Clock },
  PENDING_PAYMENT: { label: 'Zahlung ausstehend', color: 'bg-amber-100 text-amber-700', icon: CreditCard },
  ACTIVE_PREMIUM: { label: 'Premium Aktiv', color: 'bg-emerald-100 text-emerald-700', icon: Star },
  PAST_DUE: { label: 'Überfällig', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  SUSPENDED: { label: 'Gesperrt', color: 'bg-red-200 text-red-800', icon: XCircle },
  CANCELLED_ACTIVE: { label: 'Gekündigt (aktiv)', color: 'bg-orange-100 text-orange-700', icon: Clock },
  CANCELLED_EXPIRED: { label: 'Gekündigt', color: 'bg-gray-100 text-gray-600', icon: XCircle },
};

const roleLabels = {
  admin: { label: 'Admin', color: 'bg-red-100 text-red-700' },
  group_admin: { label: 'Gruppen-Admin', color: 'bg-violet-100 text-violet-700' },
  site_manager: { label: 'Standort-Admin', color: 'bg-blue-100 text-blue-700' },
  user: { label: 'Benutzer', color: 'bg-slate-100 text-slate-700' },
  external: { label: 'Extern', color: 'bg-teal-100 text-teal-700' },
};

// ─── Overview Tab ────────────────────────────────────────────────────

const OverviewTab = ({ data, onUpgrade }) => {
  const state = data.subscription_state || 'STANDARD_FREE';
  const cfg = stateConfig[state] || stateConfig.STANDARD_FREE;
  const tier = data.subscription_tier;

  return (
    <div className="space-y-6">
      <Card className="border-2 border-violet-200 dark:border-violet-800">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-violet-100 dark:bg-violet-900">
                <Layers className="h-6 w-6 text-violet-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold font-manrope" data-testid="group-name">{data.group_name}</h3>
                <Badge className={`mt-1 ${cfg.color}`} data-testid="group-state-badge">{cfg.label}</Badge>
              </div>
            </div>
            <div className="text-right">
              {tier ? (
                <>
                  <p className="text-2xl font-bold font-mono text-violet-600">
                    {data.grandfathered_price != null
                      ? `${data.grandfathered_price.toFixed(2)} EUR`
                      : `${(tier.price_monthly_net || 0).toFixed(2)} EUR`}
                  </p>
                  <p className="text-xs text-muted-foreground">pro Monat (netto)</p>
                </>
              ) : <p className="text-lg font-bold text-emerald-600">Kostenlos</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs font-medium text-muted-foreground uppercase">Standorte</p>
          <p className="text-2xl font-bold mt-1 font-mono">{data.member_site_count || 0}</p>
          {data.max_member_sites && <p className="text-xs text-muted-foreground">von max. {data.max_member_sites}</p>}
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs font-medium text-muted-foreground uppercase">Benutzer</p>
          <p className="text-2xl font-bold mt-1 font-mono">{data.group_user_count || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs font-medium text-muted-foreground uppercase">Provisionssatz</p>
          <p className="text-2xl font-bold mt-1 font-mono text-orange-600">{tier?.commission_rate || 10}%</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs font-medium text-muted-foreground uppercase">Einladungen</p>
          <p className="text-2xl font-bold mt-1 font-mono">{data.invitations?.filter(i => i.status === 'PENDING').length || 0}</p>
          <p className="text-xs text-muted-foreground">ausstehend</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs font-medium text-muted-foreground uppercase">Rechnungen</p>
          <p className="text-2xl font-bold mt-1 font-mono">{data.invoices?.length || 0}</p>
        </CardContent></Card>
      </div>

      {tier && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Tag className="h-4 w-4" />Gruppen-Tier: {tier.label}</CardTitle>
          </CardHeader>
          <CardContent>
            {tier.features?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {tier.features.map((f, i) => <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>)}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {state !== 'ACTIVE_PREMIUM' && data.available_tiers?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><ArrowUpCircle className="h-4 w-4 text-emerald-500" />Gruppen-Abo aktivieren</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {data.available_tiers.filter(t => t.price_monthly_net > 0).map(t => (
                <div key={t.tier_id} className="border rounded-lg p-4 hover:border-violet-300 transition-colors">
                  <div className="flex justify-between mb-2">
                    <h4 className="font-semibold">{t.label}</h4>
                    <p className="font-bold font-mono text-violet-600">{t.price_monthly_net} EUR<span className="text-xs font-normal">/Mo.</span></p>
                  </div>
                  {t.features?.map((f, i) => <Badge key={i} variant="outline" className="text-xs mr-1 mb-1">{f}</Badge>)}
                  <Button size="sm" className="w-full mt-3 bg-violet-600 hover:bg-violet-700" onClick={() => onUpgrade(t)} data-testid={`group-activate-${t.name}`}>
                    <ShieldCheck className="h-4 w-4 mr-1" />Jetzt aktivieren
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ─── Sites Tab (formerly Members) ────────────────────────────────────

const SitesTab = ({ members, onUpdateCostCentre, onReload }) => {
  const [editingSite, setEditingSite] = useState(null);
  const [ccValue, setCcValue] = useState('');
  const [billingDialog, setBillingDialog] = useState(null);
  const [billingForm, setBillingForm] = useState({});
  const [savingBilling, setSavingBilling] = useState(false);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleSave = async (siteId) => {
    await onUpdateCostCentre(siteId, ccValue);
    setEditingSite(null);
  };

  const openBillingDialog = async (site) => {
    setLoadingBilling(true);
    setBillingDialog(site);
    try {
      const res = await axios.get(`${API}/portal/my-group/members/${site.site_id}/billing`);
      setBillingForm(res.data);
    } catch {
      setBillingForm({
        site_id: site.site_id, site_name: site.site_name,
        legal_company_name: '', billing_address_street: '', billing_address_house_number: '',
        billing_address_postal_code: '', billing_address_city: '', billing_address_country: 'DE',
        vat_id: '', po_number: '', billing_contact_email: '', billing_contact_name: '', iban: '', bic: '',
      });
    } finally { setLoadingBilling(false); }
  };

  const handleSaveBilling = async () => {
    if (!billingDialog) return;
    setSavingBilling(true);
    try {
      const { site_id, site_name, ...payload } = billingForm;
      await axios.put(`${API}/portal/my-group/members/${billingDialog.site_id}/billing`, payload);
      toast.success(`Rechnungsdaten für ${billingDialog.site_name} gespeichert`);
      setBillingDialog(null);
      if (onReload) onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Speichern'); }
    finally { setSavingBilling(false); }
  };

  const handleSiteAction = async (action, site) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/portal/my-group/sites/${site.site_id}/${action}`);
      const msgs = { deactivate: 'Standort deaktiviert', activate: 'Standort aktiviert', remove: 'Standort aus Gruppe entfernt' };
      toast.success(msgs[action] || 'Aktion ausgeführt');
      setConfirmDialog(null);
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setActionLoading(false); }
  };

  const setBF = (k, v) => setBillingForm(prev => ({ ...prev, [k]: v }));

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Factory className="h-5 w-5" />Standorte</CardTitle>
          <CardDescription>{members.length} Standorte in Ihrer Gruppe</CardDescription>
        </CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">Noch keine Standorte</p>
          ) : (
            <div className="space-y-3">
              {members.map(site => {
                const isActive = site.is_active !== false;
                return (
                  <div key={site.site_id} className={`border rounded-lg p-4 ${!isActive ? 'opacity-60 bg-muted/30' : ''}`} data-testid={`member-site-${site.site_id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <p className="font-semibold">{site.site_name}</p>
                          <Badge className={stateConfig[site.subscription_state]?.color || 'bg-slate-100'} variant="outline">
                            {stateConfig[site.subscription_state]?.label || site.subscription_state || 'Standard'}
                          </Badge>
                          {!isActive && <Badge className="bg-red-100 text-red-700" variant="outline">Deaktiviert</Badge>}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                          {site.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{site.city}</span>}
                          <span>{site.listing_count || 0} Inserate</span>
                          {site.cost_centre_code && <span className="flex items-center gap-1"><Hash className="h-3 w-3" />{site.cost_centre_code}</span>}
                          <span className="flex items-center gap-1"><Users className="h-3 w-3" />{site.site_users?.length || 0} Benutzer</span>
                        </div>
                        {site.site_admin && (
                          <p className="text-xs text-muted-foreground mt-1">
                            <span className="font-medium text-foreground">Standort-Admin:</span> {site.site_admin.name} ({site.site_admin.email})
                          </p>
                        )}
                      </div>
                      <div className="shrink-0 flex items-center gap-2 flex-wrap justify-end">
                        <Button size="sm" variant="outline" className="gap-1" onClick={() => openBillingDialog(site)} data-testid={`billing-site-${site.site_id}`}>
                          <Building className="h-3 w-3" />Rechnungsdaten
                        </Button>
                        <Button size="sm" variant="outline" className="gap-1 text-violet-600 border-violet-200 hover:bg-violet-50" onClick={() => window.open(`${API}/portal/my-group/members/${site.site_id}/sepa-mandate`, '_blank')} data-testid={`sepa-mandate-site-${site.site_id}`}>
                          <FileText className="h-3 w-3" />SEPA
                        </Button>
                        {editingSite === site.site_id ? (
                          <div className="flex items-center gap-2">
                            <Input className="w-32 h-8 text-xs" value={ccValue} onChange={e => setCcValue(e.target.value)} placeholder="Kostenstelle" data-testid={`cc-input-${site.site_id}`} />
                            <Button size="sm" variant="outline" onClick={() => handleSave(site.site_id)} data-testid={`cc-save-${site.site_id}`}>OK</Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingSite(null)}>X</Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => { setEditingSite(site.site_id); setCcValue(site.cost_centre_code || ''); }} data-testid={`cc-edit-${site.site_id}`}>
                            <Hash className="h-3 w-3 mr-1" />Kostenstelle
                          </Button>
                        )}
                        {isActive ? (
                          <Button size="sm" variant="outline" className="gap-1 text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => setConfirmDialog({ action: 'deactivate', site })} data-testid={`deactivate-site-${site.site_id}`}>
                            <Power className="h-3 w-3" />Deaktivieren
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="gap-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => handleSiteAction('activate', site)} data-testid={`activate-site-${site.site_id}`}>
                            <CheckCircle2 className="h-3 w-3" />Aktivieren
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirmDialog({ action: 'remove', site })} data-testid={`remove-site-${site.site_id}`}>
                          <Trash2 className="h-3 w-3" />Entfernen
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={(open) => { if (!open) setConfirmDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirmDialog?.action === 'deactivate' ? 'Standort deaktivieren?' : 'Standort entfernen?'}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog?.action === 'deactivate'
                ? `Möchten Sie "${confirmDialog?.site?.site_name}" wirklich deaktivieren? Der Standort bleibt in der Gruppe, ist aber inaktiv.`
                : `Möchten Sie "${confirmDialog?.site?.site_name}" wirklich aus der Gruppe entfernen? Diese Aktion kann nicht rückgängig gemacht werden.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(null)}>Abbrechen</Button>
            <Button variant="destructive" onClick={() => handleSiteAction(confirmDialog.action, confirmDialog.site)} disabled={actionLoading} data-testid="confirm-site-action-btn">
              {actionLoading ? 'Bitte warten...' : confirmDialog?.action === 'deactivate' ? 'Deaktivieren' : 'Entfernen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Billing Dialog */}
      <Dialog open={!!billingDialog} onOpenChange={(open) => { if (!open) setBillingDialog(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />Rechnungsdaten — {billingDialog?.site_name}
            </DialogTitle>
            <DialogDescription>Rechnungsdaten und Bankverbindung des Standorts bearbeiten</DialogDescription>
          </DialogHeader>
          {loadingBilling ? (
            <div className="py-8 text-center text-muted-foreground">Laden...</div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Firma</Label>
                <Input value={billingForm.legal_company_name || ''} onChange={e => setBF('legal_company_name', e.target.value)} data-testid="site-billing-company" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Strasse</Label><Input value={billingForm.billing_address_street || ''} onChange={e => setBF('billing_address_street', e.target.value)} data-testid="site-billing-street" /></div>
                <div><Label>Hausnummer</Label><Input value={billingForm.billing_address_house_number || ''} onChange={e => setBF('billing_address_house_number', e.target.value)} data-testid="site-billing-house" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>PLZ</Label><Input value={billingForm.billing_address_postal_code || ''} onChange={e => setBF('billing_address_postal_code', e.target.value)} data-testid="site-billing-plz" /></div>
                <div><Label>Stadt</Label><Input value={billingForm.billing_address_city || ''} onChange={e => setBF('billing_address_city', e.target.value)} data-testid="site-billing-city" /></div>
              </div>
              <div><Label>Land</Label><Input value={billingForm.billing_address_country || ''} onChange={e => setBF('billing_address_country', e.target.value)} data-testid="site-billing-country" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>USt-ID</Label><Input value={billingForm.vat_id || ''} onChange={e => setBF('vat_id', e.target.value)} placeholder="z.B. DE123456789" data-testid="site-billing-vat" /></div>
                <div><Label>PO-Nummer</Label><Input value={billingForm.po_number || ''} onChange={e => setBF('po_number', e.target.value)} data-testid="site-billing-po" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Rechnungskontakt E-Mail</Label><Input type="email" value={billingForm.billing_contact_email || ''} onChange={e => setBF('billing_contact_email', e.target.value)} data-testid="site-billing-email" /></div>
                <div><Label>Rechnungskontakt Name</Label><Input value={billingForm.billing_contact_name || ''} onChange={e => setBF('billing_contact_name', e.target.value)} data-testid="site-billing-contact" /></div>
              </div>
              <div className="pt-3 border-t">
                <p className="text-sm font-semibold mb-1 flex items-center gap-2"><CreditCard className="h-4 w-4 text-muted-foreground" />Bankverbindung (SEPA)</p>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div><Label>IBAN</Label><Input value={billingForm.iban || ''} onChange={e => setBF('iban', e.target.value.replace(/\s/g, '').toUpperCase())} placeholder="DE00 0000 0000 0000 0000 00" className="font-mono" data-testid="site-billing-iban" /></div>
                  <div><Label>BIC / SWIFT</Label><Input value={billingForm.bic || ''} onChange={e => setBF('bic', e.target.value.toUpperCase())} placeholder="z.B. COBADEFFXXX" className="font-mono" data-testid="site-billing-bic" /></div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBillingDialog(null)}>Abbrechen</Button>
            <Button className="bg-violet-600 hover:bg-violet-700" onClick={handleSaveBilling} disabled={savingBilling || loadingBilling} data-testid="save-site-billing-btn">
              {savingBilling ? 'Speichern...' : 'Rechnungsdaten speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─── Users Tab ───────────────────────────────────────────────────────

const UsersTab = ({ memberSites, groupUsers, onReload }) => {
  const [expandedSites, setExpandedSites] = useState({});
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Build site→users map from memberSites data
  const sitesWithUsers = (memberSites || []).map(site => ({
    ...site,
    users: site.site_users || [],
  }));

  // Users without a site assignment
  const siteIdSet = new Set((memberSites || []).map(s => s.site_id));
  const unassignedUsers = (groupUsers || []).filter(u => !u.site_id || !siteIdSet.has(u.site_id));

  const toggleSite = (siteId) => {
    setExpandedSites(prev => ({ ...prev, [siteId]: !prev[siteId] }));
  };

  const handleUserAction = async (action, userId) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/portal/my-group/users/${userId}/${action}`);
      const msgs = { deactivate: 'Benutzer deaktiviert', activate: 'Benutzer aktiviert', remove: 'Benutzer aus Gruppe entfernt' };
      toast.success(msgs[action] || 'Aktion ausgeführt');
      setConfirmDialog(null);
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setActionLoading(false); }
  };

  const UserRow = ({ u }) => {
    const rl = roleLabels[u.role] || roleLabels.user;
    const isActive = u.is_active !== false;
    return (
      <div className={`flex items-center justify-between py-2.5 px-3 ${!isActive ? 'opacity-50' : ''}`} data-testid={`user-row-${u.user_id}`}>
        <div className="flex items-center gap-3 min-w-0">
          <UserCircle className="h-5 w-5 text-muted-foreground shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm truncate">{u.name || '—'}</span>
              <Badge className={`${rl.color} text-xs`} variant="outline">{rl.label}</Badge>
              {!isActive && <Badge className="bg-red-100 text-red-700 text-xs" variant="outline">Inaktiv</Badge>}
            </div>
            <p className="text-xs text-muted-foreground truncate">{u.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-2">
          {isActive ? (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-amber-600 hover:bg-amber-50 hover:text-amber-700" onClick={() => setConfirmDialog({ action: 'deactivate', user: u })} data-testid={`deactivate-user-${u.user_id}`}>
              <UserX className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700" onClick={() => handleUserAction('activate', u.user_id)} data-testid={`activate-user-${u.user_id}`}>
              <UserCheck className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setConfirmDialog({ action: 'remove', user: u })} data-testid={`remove-user-${u.user_id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" />Benutzer nach Standort</CardTitle>
          <CardDescription>{groupUsers?.length || 0} Benutzer in {memberSites?.length || 0} Standorten</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {sitesWithUsers.map(site => {
            const isExpanded = expandedSites[site.site_id] !== false;
            const isActive = site.is_active !== false;
            return (
              <div key={site.site_id} className={`border rounded-lg overflow-hidden ${!isActive ? 'opacity-60' : ''}`} data-testid={`site-users-group-${site.site_id}`}>
                <button
                  className="w-full flex items-center justify-between p-3 hover:bg-muted/30 transition-colors text-left"
                  onClick={() => toggleSite(site.site_id)}
                  data-testid={`toggle-site-${site.site_id}`}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                    <Factory className="h-4 w-4 text-violet-500" />
                    <span className="font-semibold text-sm">{site.site_name}</span>
                    {!isActive && <Badge className="bg-red-100 text-red-700 text-xs" variant="outline">Deaktiviert</Badge>}
                    {site.city && <span className="text-xs text-muted-foreground">({site.city})</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {site.site_admin && (
                      <span className="text-xs text-muted-foreground hidden sm:inline">
                        Admin: <span className="font-medium text-foreground">{site.site_admin.name}</span>
                      </span>
                    )}
                    <Badge variant="secondary" className="text-xs">{site.users.length} Benutzer</Badge>
                  </div>
                </button>
                {isExpanded && (
                  <div className="border-t divide-y bg-muted/10">
                    {site.users.length === 0 ? (
                      <p className="text-center py-4 text-sm text-muted-foreground">Keine Benutzer an diesem Standort</p>
                    ) : (
                      site.users.map(u => <UserRow key={u.user_id} u={u} />)
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {unassignedUsers.length > 0 && (
            <div className="border rounded-lg overflow-hidden border-dashed" data-testid="unassigned-users-group">
              <div className="flex items-center gap-3 p-3 bg-muted/20">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="font-semibold text-sm text-muted-foreground">Ohne Standortzuordnung</span>
                <Badge variant="secondary" className="text-xs">{unassignedUsers.length}</Badge>
              </div>
              <div className="border-t divide-y bg-muted/10">
                {unassignedUsers.map(u => <UserRow key={u.user_id} u={u} />)}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={(open) => { if (!open) setConfirmDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirmDialog?.action === 'deactivate' ? 'Benutzer deaktivieren?' : 'Benutzer entfernen?'}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog?.action === 'deactivate'
                ? `Möchten Sie "${confirmDialog?.user?.name || confirmDialog?.user?.email}" wirklich deaktivieren?`
                : `Möchten Sie "${confirmDialog?.user?.name || confirmDialog?.user?.email}" wirklich aus der Gruppe entfernen? Die Gruppenzuordnung wird aufgehoben.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(null)}>Abbrechen</Button>
            <Button variant="destructive" onClick={() => handleUserAction(confirmDialog.action, confirmDialog.user.user_id)} disabled={actionLoading} data-testid="confirm-user-action-btn">
              {actionLoading ? 'Bitte warten...' : confirmDialog?.action === 'deactivate' ? 'Deaktivieren' : 'Entfernen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─── Invitations Tab ─────────────────────────────────────────────────

const InvitationsTab = ({ invitations, groupId, onReload }) => {
  const [sendDialog, setSendDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [searchSites, setSearchSites] = useState([]);
  const [siteQuery, setSiteQuery] = useState('');

  const handleSendByEmail = async () => {
    if (!inviteEmail) { toast.error('E-Mail angeben'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/invitations/by-email?group_id=${groupId}`, { email: inviteEmail, personal_message: inviteMessage || null });
      toast.success('Einladung gesendet!');
      setSendDialog(false); setInviteEmail(''); setInviteMessage('');
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Senden'); }
    finally { setSending(false); }
  };

  const handleSendBySite = async (siteId) => {
    setSending(true);
    try {
      await axios.post(`${API}/invitations/by-site?group_id=${groupId}`, { site_id: siteId, personal_message: inviteMessage || null });
      toast.success('Einladung gesendet!');
      setSearchSites([]); setSiteQuery('');
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSending(false); }
  };

  const handleSearchSites = async (q) => {
    setSiteQuery(q);
    if (q.length < 2) { setSearchSites([]); return; }
    try {
      const res = await axios.get(`${API}/invitations/search-sites?q=${encodeURIComponent(q)}`);
      setSearchSites(res.data);
    } catch { setSearchSites([]); }
  };

  const handleRevoke = async (invId) => {
    try { await axios.post(`${API}/invitations/${invId}/revoke`); toast.success('Einladung widerrufen'); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleResend = async (invId) => {
    try { await axios.post(`${API}/invitations/${invId}/resend`); toast.success('Einladung erneut gesendet'); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const statusColors = {
    PENDING: 'bg-amber-100 text-amber-700', ACCEPTED: 'bg-emerald-100 text-emerald-700',
    DECLINED: 'bg-red-100 text-red-700', EXPIRED: 'bg-gray-100 text-gray-600',
    REVOKED: 'bg-slate-100 text-slate-600', SUPERSEDED: 'bg-blue-100 text-blue-600',
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2"><Send className="h-5 w-5" />Neue Einladung</CardTitle>
              <CardDescription>Laden Sie einen Standort ein, Ihrer Gruppe beizutreten</CardDescription>
            </div>
            <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => setSendDialog(true)} data-testid="send-invitation-btn">
              <Mail className="h-4 w-4 mr-1" />Per E-Mail einladen
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div>
            <Label className="text-sm font-medium mb-2 block">Oder: Standort suchen</Label>
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={siteQuery} onChange={e => handleSearchSites(e.target.value)} placeholder="Standort-Name eingeben..." className="pl-10" data-testid="search-site-input" />
            </div>
            {searchSites.length > 0 && (
              <div className="mt-2 border rounded-lg divide-y max-w-md">
                {searchSites.map(s => (
                  <div key={s.site_id} className="flex items-center justify-between p-3">
                    <div>
                      <p className="text-sm font-medium">{s.site_name}</p>
                      <p className="text-xs text-muted-foreground">{s.city}, {s.country}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleSendBySite(s.site_id)} disabled={sending} data-testid={`invite-site-${s.site_id}`}>
                      <Send className="h-3 w-3 mr-1" />Einladen
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" />Versendete Einladungen</CardTitle>
          <CardDescription>{invitations.length} Einladungen gesamt</CardDescription>
        </CardHeader>
        <CardContent>
          {invitations.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">Noch keine Einladungen versendet</p>
          ) : (
            <div className="space-y-3">
              {invitations.map(inv => (
                <div key={inv.invitation_id} className="border rounded-lg p-4" data-testid={`group-inv-${inv.invitation_id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium">{inv.invited_site_name || inv.invited_email}</p>
                        <Badge className={statusColors[inv.status] || 'bg-gray-100'}>{inv.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        ID: {inv.invitation_id} | Gesendet: {(inv.sent_at || '').substring(0, 10)}
                        {inv.expiry_at && ` | Gültig bis: ${inv.expiry_at.substring(0, 10)}`}
                      </p>
                      {inv.decline_reason && <p className="text-xs text-red-600 mt-1">Grund: {inv.decline_reason}</p>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {inv.status === 'PENDING' && (
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleRevoke(inv.invitation_id)} data-testid={`revoke-${inv.invitation_id}`}>Widerrufen</Button>
                      )}
                      {(inv.status === 'EXPIRED' || inv.status === 'DECLINED') && (
                        <Button size="sm" variant="outline" onClick={() => handleResend(inv.invitation_id)} data-testid={`resend-${inv.invitation_id}`}>Erneut senden</Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={sendDialog} onOpenChange={setSendDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Einladung per E-Mail</DialogTitle>
            <DialogDescription>Geben Sie die E-Mail-Adresse des Standort-Admins ein</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>E-Mail</Label><Input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="admin@firma.de" data-testid="invite-email-input" /></div>
            <div><Label>Persönliche Nachricht (optional)</Label><Textarea value={inviteMessage} onChange={e => setInviteMessage(e.target.value)} placeholder="Wir möchten Sie einladen..." maxLength={500} data-testid="invite-message" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialog(false)}>Abbrechen</Button>
            <Button className="bg-violet-600 hover:bg-violet-700" onClick={handleSendByEmail} disabled={sending} data-testid="confirm-send-invite">
              {sending ? 'Senden...' : 'Einladung senden'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── Invoices Tab ────────────────────────────────────────────────────

const GroupInvoicesTab = ({ invoices }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2"><Receipt className="h-5 w-5" />Gruppen-Rechnungen</CardTitle>
      <CardDescription>{invoices.length} Rechnungen</CardDescription>
    </CardHeader>
    <CardContent>
      {invoices.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">Noch keine Rechnungen</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="pb-2 font-medium text-muted-foreground">Nr.</th>
                <th className="pb-2 font-medium text-muted-foreground">Datum</th>
                <th className="pb-2 font-medium text-muted-foreground">Netto</th>
                <th className="pb-2 font-medium text-muted-foreground">MwSt.</th>
                <th className="pb-2 font-medium text-muted-foreground">Brutto</th>
                <th className="pb-2 font-medium text-muted-foreground">Status</th>
                <th className="pb-2 font-medium text-muted-foreground"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.invoice_id} className="border-b last:border-0 hover:bg-muted/30" data-testid={`group-invoice-${inv.invoice_number}`}>
                  <td className="py-2.5 font-mono text-xs">{inv.invoice_number}</td>
                  <td className="py-2.5 text-xs">{inv.invoice_date}</td>
                  <td className="py-2.5 font-mono">{(inv.total_net || 0).toFixed(2)} EUR</td>
                  <td className="py-2.5 font-mono text-muted-foreground">{(inv.vat_amount || 0).toFixed(2)} EUR</td>
                  <td className="py-2.5 font-mono font-bold">{(inv.total_gross || 0).toFixed(2)} EUR</td>
                  <td className="py-2.5">
                    <Badge variant="outline" className={inv.payment_status === 'PAID' ? 'text-emerald-600' : 'text-amber-600'}>
                      {inv.payment_status === 'PAID' ? 'Bezahlt' : inv.payment_status === 'OPEN' ? 'Offen' : inv.payment_status}
                    </Badge>
                  </td>
                  <td className="py-2.5">
                    <Button size="sm" variant="ghost" onClick={() => window.open(`${API}/invoices/${inv.invoice_id}/pdf`, '_blank')} data-testid={`dl-group-inv-${inv.invoice_number}`}>
                      <Download className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CardContent>
  </Card>
);

// ─── Billing Settings Tab ────────────────────────────────────────────

const GroupBillingTab = ({ billing, onSave, saving }) => {
  const [form, setForm] = useState(billing || {});
  useEffect(() => { if (billing) setForm(billing); }, [billing]);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-6">
      <Card className="border-violet-200 dark:border-violet-800">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-400">
                <Receipt className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold">SEPA-Lastschriftmandat</p>
                <p className="text-xs text-muted-foreground">PDF-Mandat herunterladen, unterschreiben und zurücksenden</p>
              </div>
            </div>
            <Button variant="outline" className="gap-2 border-violet-200 text-violet-600 hover:bg-violet-50" onClick={() => window.open(`${API}/portal/my-group/sepa-mandate`, '_blank')} data-testid="download-group-sepa-mandate-btn">
              <Download className="h-4 w-4" />SEPA-Mandat PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Building className="h-5 w-5" />Gruppen-Rechnungsdaten</CardTitle>
          <CardDescription>Diese Daten erscheinen auf den Gruppen-Rechnungen und im SEPA-Mandat</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><Label>Firma</Label><Input value={form.legal_company_name || ''} onChange={e => set('legal_company_name', e.target.value)} data-testid="group-billing-company" /></div>
            <div><Label>Strasse</Label><Input value={form.billing_address_street || ''} onChange={e => set('billing_address_street', e.target.value)} data-testid="group-billing-street" /></div>
            <div><Label>Hausnummer</Label><Input value={form.billing_address_house_number || ''} onChange={e => set('billing_address_house_number', e.target.value)} data-testid="group-billing-house" /></div>
            <div><Label>PLZ</Label><Input value={form.billing_address_postal_code || ''} onChange={e => set('billing_address_postal_code', e.target.value)} data-testid="group-billing-plz" /></div>
            <div><Label>Stadt</Label><Input value={form.billing_address_city || ''} onChange={e => set('billing_address_city', e.target.value)} data-testid="group-billing-city" /></div>
            <div><Label>Land</Label><Input value={form.billing_address_country || ''} onChange={e => set('billing_address_country', e.target.value)} data-testid="group-billing-country" /></div>
            <div><Label>USt-ID</Label><Input value={form.vat_id || ''} onChange={e => set('vat_id', e.target.value)} placeholder="z.B. DE123456789" data-testid="group-billing-vat" /></div>
            <div><Label>PO-Nummer</Label><Input value={form.po_number || ''} onChange={e => set('po_number', e.target.value)} data-testid="group-billing-po" /></div>
            <div><Label>Rechnungskontakt E-Mail</Label><Input type="email" value={form.billing_contact_email || ''} onChange={e => set('billing_contact_email', e.target.value)} data-testid="group-billing-email" /></div>
            <div><Label>Rechnungskontakt Name</Label><Input value={form.billing_contact_name || ''} onChange={e => set('billing_contact_name', e.target.value)} data-testid="group-billing-contact" /></div>
          </div>
          <div className="mt-6 pt-6 border-t">
            <p className="text-sm font-semibold mb-1 flex items-center gap-2"><CreditCard className="h-4 w-4 text-muted-foreground" />Bankverbindung (für SEPA-Lastschrift)</p>
            <p className="text-xs text-muted-foreground mb-4">Wird im SEPA-Mandat vorausgefüllt</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>IBAN</Label><Input value={form.iban || ''} onChange={e => set('iban', e.target.value.replace(/\s/g, '').toUpperCase())} placeholder="DE00 0000 0000 0000 0000 00" className="font-mono" data-testid="group-billing-iban" /></div>
              <div><Label>BIC / SWIFT</Label><Input value={form.bic || ''} onChange={e => set('bic', e.target.value.toUpperCase())} placeholder="z.B. COBADEFFXXX" className="font-mono" data-testid="group-billing-bic" /></div>
            </div>
          </div>
          <Button className="mt-6 bg-violet-600 hover:bg-violet-700" onClick={() => onSave(form)} disabled={saving} data-testid="save-group-billing-btn">
            {saving ? 'Speichern...' : 'Rechnungsdaten speichern'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────

export default function GroupAdminPortalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [savingBilling, setSavingBilling] = useState(false);
  const [activateDialog, setActivateDialog] = useState(null);
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [datenschutzAccepted, setDatenschutzAccepted] = useState(false);
  const [activating, setActivating] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/portal/my-group`);
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 403) { toast.error('Kein Zugang'); navigate('/'); }
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!user) { navigate('/login'); return null; }

  const handleUpgrade = (tier) => {
    setActivateDialog(tier);
    setAgbAccepted(false);
    setDatenschutzAccepted(false);
  };

  const handleActivate = async () => {
    if (!agbAccepted || !datenschutzAccepted) {
      toast.error('Bitte akzeptieren Sie die AGB und Datenschutzbestimmungen');
      return;
    }
    if (!activateDialog || !data?.group_id) return;
    setActivating(true);
    try {
      await axios.post(`${API}/portal/my-group/activate`, {
        tier_id: activateDialog.tier_id,
        agb_accepted: true,
        datenschutz_accepted: true,
        agb_version: 'v1.0',
      });
      toast.success('Gruppen-Abonnement erfolgreich aktiviert!');
      setActivateDialog(null);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aktivierung fehlgeschlagen');
    } finally { setActivating(false); }
  };

  const handleSaveBilling = async (form) => {
    setSavingBilling(true);
    try {
      await axios.put(`${API}/portal/my-group/billing`, form);
      toast.success('Rechnungsdaten gespeichert');
      setData(prev => ({ ...prev, billing: form }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSavingBilling(false); }
  };

  const handleUpdateCostCentre = async (siteId, code) => {
    try {
      await axios.put(`${API}/portal/my-group/members/${siteId}/cost-centre?cost_centre_code=${encodeURIComponent(code)}`);
      toast.success('Kostenstelle aktualisiert');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  if (loading) return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-4 gap-4">{[0,1,2,3].map(i => <Skeleton key={i} className="h-28" />)}</div>
        <Skeleton className="h-96" />
      </div>
    </div>
  );

  if (!data || !data.has_group) return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-16 text-center">
        <Layers className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Keine Gruppe zugeordnet</h2>
        <p className="text-muted-foreground">Sie sind keiner Unternehmensgruppe als Admin zugeordnet.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="group-admin-portal">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold font-manrope" data-testid="group-admin-title">Gruppen-Admin Portal</h1>
            <p className="text-sm text-muted-foreground mt-1">Verwalten Sie Ihre Gruppe, Standorte, Benutzer und Abrechnung</p>
          </div>
          <Badge className="bg-violet-100 text-violet-700" data-testid="group-name-badge">
            <Layers className="h-3 w-3 mr-1" />{data.group_name}
          </Badge>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`mb-6 grid w-full ${data.api_docs_enabled ? 'grid-cols-7' : 'grid-cols-6'}`}>
            <TabsTrigger value="overview" data-testid="tab-group-overview">Übersicht</TabsTrigger>
            <TabsTrigger value="sites" data-testid="tab-group-sites">Standorte</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-group-users">Benutzer</TabsTrigger>
            <TabsTrigger value="invitations" data-testid="tab-group-invitations">Einladungen</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-group-invoices">Rechnungen</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-group-settings">Einstellungen</TabsTrigger>
            {data.api_docs_enabled && (
              <TabsTrigger value="api-docs" data-testid="tab-group-api-docs" className="gap-1.5">
                <BookOpen className="h-3.5 w-3.5" />API-Doku
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab data={data} onUpgrade={handleUpgrade} />
          </TabsContent>
          <TabsContent value="sites">
            <SitesTab members={data.member_sites || []} onUpdateCostCentre={handleUpdateCostCentre} onReload={loadData} />
          </TabsContent>
          <TabsContent value="users">
            <UsersTab memberSites={data.member_sites || []} groupUsers={data.group_users || []} onReload={loadData} />
          </TabsContent>
          <TabsContent value="invitations">
            <InvitationsTab invitations={data.invitations || []} groupId={data.group_id} onReload={loadData} />
          </TabsContent>
          <TabsContent value="invoices">
            <GroupInvoicesTab invoices={data.invoices || []} />
          </TabsContent>
          <TabsContent value="settings">
            <GroupBillingTab billing={data.billing} onSave={handleSaveBilling} saving={savingBilling} />
          </TabsContent>
          {data.api_docs_enabled && (
            <TabsContent value="api-docs">
              <div className="space-y-4" data-testid="group-api-docs-tab-content">
                <Card className="border-violet-200 dark:border-violet-800">
                  <CardContent className="pt-5 pb-5 flex items-start gap-4">
                    <div className="p-2.5 rounded-lg bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-400">
                      <BookOpen className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-semibold">API- &amp; Schnittstellen-Dokumentation</p>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        Diese Dokumentation wurde für Ihre Gruppe <span className="font-medium text-foreground">{data.group_name}</span> durch den Plattform-Administrator freigeschaltet. Standorte Ihrer Gruppe können die API zusätzlich pro Standort (Site) aktivieren.
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <ApiDocsSection enabled={true} />
              </div>
            </TabsContent>
          )}
        </Tabs>

        {/* Activation Dialog */}
        <Dialog open={!!activateDialog} onOpenChange={(v) => !v && setActivateDialog(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-violet-500" />
                Gruppen-Abonnement aktivieren
              </DialogTitle>
              <DialogDescription>
                Aktivieren Sie das Premium-Abonnement für <span className="font-semibold">{data?.group_name}</span>
              </DialogDescription>
            </DialogHeader>
            {activateDialog && (
              <div className="space-y-5">
                <div className="border rounded-lg p-4 bg-violet-50/50 dark:bg-violet-950/20">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-lg">{activateDialog.label}</p>
                      <p className="text-sm text-muted-foreground">Provisionssatz: {activateDialog.commission_rate}%</p>
                      {activateDialog.max_member_sites && (
                        <p className="text-sm text-muted-foreground">Max. Standorte: {activateDialog.max_member_sites}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold font-mono text-violet-600">{activateDialog.price_monthly_net} EUR</p>
                      <p className="text-xs text-muted-foreground">pro Monat (netto)</p>
                    </div>
                  </div>
                  {activateDialog.features?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t">
                      {activateDialog.features.map((f, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-3 border rounded-lg p-4">
                  <p className="text-sm font-semibold">Zustimmung erforderlich</p>
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="group-agb-checkbox-label">
                    <input
                      type="checkbox"
                      checked={agbAccepted}
                      onChange={(e) => setAgbAccepted(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 accent-violet-500"
                      data-testid="group-agb-checkbox"
                    />
                    <span className="text-sm">
                      Ich habe die <a href="/agb" target="_blank" rel="noopener" className="text-violet-600 underline underline-offset-2 hover:text-violet-700">Allgemeinen Geschäftsbedingungen (AGB)</a> gelesen und akzeptiere diese.
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="group-datenschutz-checkbox-label">
                    <input
                      type="checkbox"
                      checked={datenschutzAccepted}
                      onChange={(e) => setDatenschutzAccepted(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 accent-violet-500"
                      data-testid="group-datenschutz-checkbox"
                    />
                    <span className="text-sm">
                      Ich habe die <a href="/datenschutz" target="_blank" rel="noopener" className="text-violet-600 underline underline-offset-2 hover:text-violet-700">Datenschutzbestimmungen</a> gelesen und akzeptiere diese.
                    </span>
                  </label>
                </div>

                <p className="text-xs text-muted-foreground">
                  Mit der Aktivierung wird eine Rechnung erstellt. Die Zahlung erfolgt per SEPA-Lastschrift.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setActivateDialog(null)}>Abbrechen</Button>
              <Button
                className="bg-violet-600 hover:bg-violet-700"
                onClick={handleActivate}
                disabled={activating || !agbAccepted || !datenschutzAccepted}
                data-testid="confirm-group-activate-btn"
              >
                {activating ? 'Aktivierung...' : 'Kostenpflichtig aktivieren'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
